// src/components/themes/default/admin/analytics/CategoryAnalyticsView.tsx
"use client";

import { useState, useMemo } from "react";
import {
  Layers,
  DollarSign,
  Download,
  Search,
  CheckCircle2,
  Award,
  CircleDollarSign,
} from "lucide-react";

interface CategoryAnalyticsProps {
  data: any;
  isLight: boolean;
  timeRange: "7d" | "30d" | "90d";
  onRefresh?: () => void;
}

export default function CategoryAnalyticsView({
  data,
  isLight,
  timeRange,
}: CategoryAnalyticsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredTreemapTile, setHoveredTreemapTile] = useState<any | null>(null);
  const [hoveredBenchmarkItem, setHoveredBenchmarkItem] = useState<any | null>(null);

  const categoryRevenue = data?.category_revenue_share || [];
  const fbBenchmarks = data?.fb_market_benchmark || [];
  const totalCategoriesCount = data?.total_categories_count || categoryRevenue.length || 0;

  const categoryKpis = useMemo(() => {
    const topCategory = categoryRevenue.length > 0 ? categoryRevenue[0] : null;

    const totalRev = categoryRevenue.reduce((acc: number, curr: any) => {
      const numStr = String(curr.amount || "").replace(/[^0-9.]/g, "");
      return acc + (parseFloat(numStr) || 0);
    }, 0);

    const cheaperItemsCount = fbBenchmarks.filter((b: any) =>
      b.position?.toLowerCase().includes("lower") || b.position?.toLowerCase().includes("cheaper")
    ).length;
    const competitiveIndex = fbBenchmarks.length > 0
      ? Math.round((cheaperItemsCount / fbBenchmarks.length) * 100)
      : 80;

    return {
      totalCategoriesCount,
      topCategoryName: topCategory ? topCategory.category : "N/A",
      topCategoryShare: topCategory ? topCategory.share : 0,
      totalRevenueAmount: totalRev,
      competitiveIndex,
    };
  }, [categoryRevenue, fbBenchmarks, totalCategoriesCount]);

  const fullTreemapBlocks = useMemo(() => {
    const colors = [
      "bg-rose-600",
      "bg-pink-600",
      "bg-blue-600",
      "bg-cyan-600",
      "bg-amber-600",
      "bg-orange-600",
      "bg-emerald-600",
      "bg-teal-600",
      "bg-indigo-600",
      "bg-purple-600",
    ];

    const spans = [
      "col-span-12 sm:col-span-7 row-span-4 min-h-[140px]",
      "col-span-12 sm:col-span-5 row-span-2 min-h-[75px]",
      "col-span-6 sm:col-span-3 row-span-2 min-h-[75px]",
      "col-span-6 sm:col-span-2 row-span-2 min-h-[75px]",
      "col-span-6 sm:col-span-4 row-span-2 min-h-[75px]",
    ];

    return categoryRevenue.map((c: any, idx: number) => ({
      name: c.category,
      share: `${c.share}%`,
      amount: c.amount,
      col: `${spans[idx % spans.length]} ${colors[idx % colors.length]}`,
    }));
  }, [categoryRevenue]);

  const filteredCategoryList = useMemo(() => {
    return categoryRevenue.filter((c: any) => {
      return c.category?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [categoryRevenue, searchTerm]);

  const handleExportCSV = () => {
    if (!categoryRevenue.length) return;
    const headers = ["Category Name", "Realized Revenue (BDT)", "Revenue Share (%)"];
    const rows = categoryRevenue.map((c: any) => [
      `"${c.category}"`,
      `"${c.amount}"`,
      `${c.share}%`,
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `category-benchmark-analytics-${timeRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* KPI Ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Tracked Taxonomies</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-blue-50 text-blue-600" : "bg-blue-500/10 text-blue-400"}`}>
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight">
            {categoryKpis.totalCategoriesCount} Categories
          </div>
          <div className="flex items-center gap-1.5 pt-1 text-xs font-bold text-emerald-500">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Category & Product Model</span>
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Dominant Category</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-emerald-50 text-emerald-600" : "bg-emerald-500/10 text-emerald-400"}`}>
              <Award className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-black truncate text-emerald-600 dark:text-emerald-400">
            {categoryKpis.topCategoryName}
          </div>
          <div className="text-xs text-slate-400 font-bold pt-1 font-mono">
            {categoryKpis.topCategoryShare}% of total gross revenue
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">FB Price Competitiveness</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-amber-50 text-amber-600" : "bg-amber-500/10 text-amber-400"}`}>
              <CircleDollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-amber-600 dark:text-amber-400">
            {categoryKpis.competitiveIndex}% Competitive
          </div>
          <div className="text-xs text-amber-500 font-bold pt-1">Lower than retail average</div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Attributed Department Sales</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-purple-50 text-purple-600" : "bg-purple-500/10 text-purple-400"}`}>
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight">
            ৳{categoryKpis.totalRevenueAmount.toLocaleString()}
          </div>
          <div className="text-xs text-purple-500 font-bold pt-1">OrderItem & Category sync</div>
        </div>
      </div>

      {/* Row 1: Treemap & Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-4 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Category & Department Treemap Share
              </h3>
              <p className="text-xs text-slate-500 font-medium">Proportional sales weight aggregated from Category</p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 font-mono">
              Treemap Matrix
            </span>
          </div>

          <div className="max-h-[300px] overflow-y-auto pr-1.5 scrollbar-thin scrollbar-thumb-slate-700">
            <div className="grid grid-cols-12 auto-rows-fr gap-2.5 text-white font-bold p-1">
              {fullTreemapBlocks.map((tile: any, idx: number) => {
                const isHovered = hoveredTreemapTile?.name === tile.name;
                return (
                  <div
                    key={idx}
                    onMouseEnter={() => setHoveredTreemapTile(tile)}
                    onMouseLeave={() => setHoveredTreemapTile(null)}
                    className={`${tile.col} rounded-2xl p-3 flex flex-col justify-between shadow-xs transition-all duration-300 cursor-pointer ${
                      isHovered ? "scale-[1.02] ring-2 ring-white z-20 shadow-xl" : "hover:opacity-95"
                    }`}
                  >
                    <div>
                      <span className="text-xs uppercase tracking-wider block font-black leading-tight truncate">
                        {tile.name}
                      </span>
                    </div>
                    <div className="mt-2">
                      <span className="text-base sm:text-lg font-mono font-black block leading-none">
                        {tile.share}
                      </span>
                      <span className="text-[10px] font-mono opacity-90 mt-0.5 block">{tile.amount}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-4 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Sales Contribution Ranking
              </h3>
              <p className="text-xs text-slate-500 font-medium">Relative revenue share per department</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-emerald-500">Revenue %</span>
          </div>

          <div className="space-y-3.5 pt-1 max-h-[300px] overflow-y-auto pr-1 scrollbar-thin">
            {categoryRevenue.map((c: any, idx: number) => (
              <div key={idx} className="space-y-1.5">
                <div className="flex justify-between text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    <span className="font-mono text-emerald-600 font-black">#{idx + 1}</span>
                    {c.category}
                  </span>
                  <span className="font-mono">
                    {c.amount} ({c.share}%)
                  </span>
                </div>
                <div className={`w-full h-2.5 rounded-full overflow-hidden ${isLight ? "bg-slate-200" : "bg-slate-800"}`}>
                  <div
                    className={`${c.color} h-full rounded-full transition-all duration-700`}
                    style={{ width: `${Math.max(2, c.share)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div
            className={`p-3.5 rounded-2xl border text-[11px] font-medium leading-relaxed ${
              isLight ? "bg-slate-50 border-slate-200 text-slate-600" : "bg-slate-950/60 border-slate-800 text-slate-400"
            }`}
          >
            💡 শীর্ষ ক্যাটাগরিগুলোর পর্যাপ্ত স্টক বজায় রাখা রাজস্ব সুরক্ষার জন্য অত্যন্ত জরুরি।
          </div>
        </div>
      </div>

      {/* Row 2: Price Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-4 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Store Selling Price vs Regular Retail / Market Price (BDT)
              </h3>
              <p className="text-xs text-slate-500 font-medium">Selling Price (Green) vs Regular Price (Amber)</p>
            </div>
            <div className="flex items-center gap-3 text-xs font-bold font-mono">
              <span className="flex items-center gap-1.5"><div className="w-3 h-3 bg-emerald-500 rounded-none" /> Your Price</span>
              <span className="flex items-center gap-1.5"><div className="w-3 h-3 bg-amber-500 rounded-none" /> Regular Price</span>
            </div>
          </div>

          <div className="relative pt-4">
            <div className="overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-700 select-none">
              <div className="h-64 flex items-end justify-start gap-2.5 px-2 min-w-max">
                {fbBenchmarks.map((b: any, idx: number) => {
                  const maxVal = Math.max(...fbBenchmarks.map((item: any) => Math.max(item.store_price, item.fb_price)), 100);
                  const storeHeight = Math.min(80, Math.max(6, (b.store_price / maxVal) * 80));
                  const marketHeight = Math.min(80, Math.max(6, (b.fb_price / maxVal) * 80));
                  const isHovered = hoveredBenchmarkItem?.product === b.product;

                  return (
                    <div
                      key={idx}
                      onMouseEnter={() => setHoveredBenchmarkItem(b)}
                      onMouseLeave={() => setHoveredBenchmarkItem(null)}
                      className="w-18 min-w-[68px] shrink-0 flex flex-col items-center justify-end h-full group relative cursor-pointer"
                    >
                      {isHovered && (
                        <div className="absolute -top-12 px-3 py-1.5 rounded-xl bg-black text-white text-[10px] font-mono font-bold pointer-events-none whitespace-nowrap z-30 shadow-xl animate-in fade-in">
                          <div>{b.product}</div>
                          <div className="text-emerald-400">Store: ৳{b.store_price.toLocaleString()}</div>
                          <div className="text-amber-400">Regular: ৳{b.fb_price.toLocaleString()}</div>
                        </div>
                      )}

                      <div className="w-full flex items-end justify-center gap-1 h-52 pb-0.5">
                        <div className="flex flex-col items-center justify-end h-full w-7">
                          <span className="text-[10px] font-mono font-black mb-1 leading-none select-none text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                            {Math.round(b.store_price)}
                          </span>
                          <div className="w-full bg-emerald-500 rounded-none transition-all duration-300" style={{ height: `${storeHeight}%` }} />
                        </div>
                        <div className="flex flex-col items-center justify-end h-full w-7">
                          <span className="text-[10px] font-mono font-black mb-1 leading-none select-none text-amber-600 dark:text-amber-400 whitespace-nowrap">
                            {Math.round(b.fb_price)}
                          </span>
                          <div className="w-full bg-amber-500 rounded-none transition-all duration-300" style={{ height: `${marketHeight}%` }} />
                        </div>
                      </div>

                      <span className="text-[10px] font-bold text-slate-400 truncate w-full text-center mt-2 group-hover:text-white transition-colors">
                        {b.product}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`border-b-2 mt-1 ${isLight ? "border-slate-300" : "border-slate-800"}`} />
          </div>
        </div>

        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-4 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Gross Profit Margin % Matrix
              </h3>
              <p className="text-xs text-slate-500 font-medium">Estimated profitability by category</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-emerald-500">Margin Matrix</span>
          </div>

          <div className="space-y-3 pt-1">
            {[
              { cat: "Fashion & Apparel", margin: "42% Gross Margin", pct: 42, color: "bg-emerald-500" },
              { cat: "Health & Organic", margin: "34% Gross Margin", pct: 34, color: "bg-teal-500" },
              { cat: "Gadgets & Accessories", margin: "18% Gross Margin", pct: 18, color: "bg-blue-500" },
              { cat: "Home & Grocery", margin: "14% Gross Margin", pct: 14, color: "bg-amber-500" },
            ].map((m, idx) => (
              <div
                key={idx}
                className={`p-3.5 rounded-2xl border-2 space-y-1.5 ${
                  isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
                }`}
              >
                <div className="flex justify-between text-xs font-bold">
                  <span>{m.cat}</span>
                  <span className="font-mono text-emerald-600 dark:text-emerald-400">{m.margin}</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                  <div className={`${m.color} h-full rounded-full transition-all duration-700`} style={{ width: `${m.pct * 2}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div
            className={`p-3.5 rounded-2xl border text-xs font-bold flex items-center justify-between ${
              isLight ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-emerald-950/20 border-emerald-800/40 text-emerald-300"
            }`}
          >
            <span>Target Gross Margin:</span>
            <span className="font-mono font-black">&gt; 25% Threshold</span>
          </div>
        </div>
      </div>

      {/* Row 3: Category Table */}
      <div
        className={`p-6 rounded-3xl border-2 space-y-4 transition-all ${
          isLight
            ? "bg-white border-slate-300 shadow-sm text-slate-900"
            : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4 border-slate-100 dark:border-slate-800/80">
          <div>
            <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
              Department Performance & Contribution Drilldown
            </h3>
            <p className="text-xs text-slate-500 font-medium">Live database records aggregated from Category</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 pr-3 py-1.5 rounded-xl border text-xs font-bold outline-none transition-all ${
                  isLight
                    ? "bg-slate-50 border-slate-300 text-slate-900 focus:bg-white"
                    : "bg-slate-950 border-slate-800 text-white focus:border-emerald-500"
                }`}
              />
            </div>

            <button
              onClick={handleExportCSV}
              className={`px-3 py-1.5 rounded-xl border-2 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                isLight
                  ? "bg-white hover:bg-slate-100 border-slate-300 text-slate-800"
                  : "bg-slate-800 hover:bg-slate-700 border-slate-700 text-white"
              }`}
              title="Download CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full text-left text-xs min-w-[550px] border-collapse">
            <thead
              className={`uppercase text-[10px] font-black border-y-2 ${
                isLight ? "bg-slate-100 text-slate-800 border-slate-300" : "bg-slate-950/60 text-slate-400 border-slate-800"
              }`}
            >
              <tr>
                <th className="py-3 px-4">Rank</th>
                <th className="py-3 px-4">Category Name</th>
                <th className="py-3 px-4">Realized Revenue</th>
                <th className="py-3 px-4">Revenue Share</th>
              </tr>
            </thead>
            <tbody className="divide-y font-medium">
              {filteredCategoryList.map((cat: any, idx: number) => (
                <tr
                  key={idx}
                  className={`border-b transition-colors ${
                    isLight
                      ? "border-slate-200 even:bg-slate-50/60 hover:bg-slate-100 text-slate-900"
                      : "border-slate-800/60 hover:bg-slate-800/30 text-slate-300"
                  }`}
                >
                  <td className="py-3.5 px-4 font-mono font-black text-blue-600 dark:text-emerald-400">
                    #{idx + 1}
                  </td>
                  <td className={`py-3.5 px-4 font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                    {cat.category}
                  </td>
                  <td className="py-3.5 px-4 font-mono font-black text-emerald-600 dark:text-emerald-400">
                    {cat.amount}
                  </td>
                  <td className="py-3.5 px-4 font-mono font-bold">{cat.share}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}